(() => {
  'use strict';

  if (window.mpContactFormLoaded) return;
  window.mpContactFormLoaded = true;
  const states = new WeakMap();

  async function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    // La app puede tardar en un arranque en frío; el mensaje se guarda aunque el navegador corte antes.
    const timeout = window.setTimeout(() => controller.abort(), 25000);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function createIdempotencyKey() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `mp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }

  function setBusy(button, busy) {
    if (!button) return;
    button.disabled = busy;
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  function showSuccess(root, form, name) {
    const panel = root.querySelector('[data-mp-contact-success]');
    const message = root.querySelector('[data-mp-contact-success-message]');
    message.textContent = `Gracias, ${name}. Hemos recibido tu mensaje y te responderemos lo antes posible.`;
    form.hidden = true;
    panel.hidden = false;
    message.focus({ preventScroll: true });
  }

  const SEND_ERROR = 'No hemos podido enviar tu mensaje. Vuelve a intentarlo en unos segundos o escríbenos por WhatsApp al 649 757 426.';

  // Sin querySelector en el camino de éxito: el aviso se recuerda por sección.
  const sendErrors = new WeakMap();

  function clearSendError(root) {
    const error = sendErrors.get(root);
    if (!error) return;
    error.hidden = true;
    error.textContent = '';
  }

  function showSendError(root, form) {
    let error = sendErrors.get(root);
    if (!error) {
      error = document.createElement('p');
      error.setAttribute('role', 'alert');
      error.className = 'form__message';
      const button = form.querySelector('button[type="submit"]');
      (button?.parentElement || form).insertBefore(error, button || null);
      sendErrors.set(root, error);
    }
    error.hidden = false;
    error.textContent = SEND_ERROR;
  }

  // Captura en window, antes del captcha de Shopify, como en el alta profesional.
  // La delegación también cubre las secciones recargadas en el editor del tema.
  window.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form.matches?.('[data-mp-contact-form]')) return;
    const root = form.closest('[data-mp-contact-root]');
    if (!root) return;
    let state = states.get(form);
    if (!state) {
      state = { sending: false, completed: false };
      states.set(form, state);
    }
    // Solo la app recibe el mensaje (aviso a Carla). Nunca el envío nativo de Shopify, que va a info@.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (state.sending || state.completed) return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Algunas maquetaciones pintan dos campos con el mismo nombre: vale el primero con contenido.
    const value = (name) => {
      const item = form.elements.namedItem(name);
      // RadioNodeList (dos campos con el mismo nombre): su .value solo sirve para radios.
      const fields = item && typeof item.length === 'number' && !item.tagName ? [...item] : [item];
      return (fields.map((field) => field?.value || '').find((text) => text.trim()) || '').trim();
    };
    const name = value('contact[name]');
    // No reenviar bots al buzón de Shopify, ni siquiera si la app no está disponible.
    if (value('contact[website]')) {
      state.completed = true;
      showSuccess(root, form, name);
      return;
    }

    const button = form.querySelector('button[type="submit"]');
    state.sending = true;
    setBusy(button, true);
    const endpoint = (root.dataset.endpoint || '').trim();
    clearSendError(root);
    if (endpoint) {
      try {
        const payload = {
          kind: 'contact',
          name,
          email: value('contact[email]'),
          phone: value('contact[phone]'),
          subject: value('contact[asunto]'),
          message: [value('contact[empresa]') && `Empresa: ${value('contact[empresa]')}`, value('contact[body]')].filter(Boolean).join('\n'),
          privacy_accepted: true,
          marketing_consent: Boolean(form.elements.namedItem('contact[marketing_consent]')?.checked),
          consent_text_version: value('consent_text_version'),
          source_page: window.location.pathname,
          website: value('contact[website]'),
          idempotency_key: ''
        };
        const population = value('contact[poblacion]') || value('contact[Población]');
        if (population) payload.population = population;
        const customerType = form.querySelector('input[name="contact[tipo_cliente]"]:checked')?.value;
        if (customerType) payload.customer_type = customerType;
        // Reintento con los mismos datos = misma clave: la app no vuelve a avisar a Carla.
        const signature = JSON.stringify(payload);
        if (state.retry?.signature !== signature) state.retry = { signature, key: createIdempotencyKey() };
        payload.idempotency_key = state.retry.key;
        const attribution = typeof window.mpAttribution === 'function' ? window.mpAttribution() : null;
        if (attribution) payload.attribution = attribution;
        const response = await fetchWithTimeout(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-MP-Form-Secret': root.dataset.secret },
          // Igual que el presupuesto: idempotencia en el cuerpo para respetar CORS.
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error(`endpoint ${response.status}`);
        state.completed = true;
      } catch (_error) {
        // Sin respaldo en Shopify: el cliente ve el error y puede reintentar o escribir por WhatsApp.
      }
    }
    state.sending = false;
    setBusy(button, false);
    if (state.completed) {
      // Un fallo al mostrar la confirmación no debe duplicar el mensaje en Shopify.
      showSuccess(root, form, name);
      return;
    }
    showSendError(root, form);
  }, true);
})();
